import {useEffect, useRef, useState} from "preact/hooks";

// Parses a MySQL "YYYY-MM-DD HH:MM:SS" datetime string (server clock) into a Date.
// Both event windows and server_time are parsed the same way, so comparisons
// against a skew-corrected local clock are consistent regardless of timezone.
export function parseServerDate(value) {
    if (!value) return null;
    const date = new Date(String(value).replace(" ", "T"));
    return Number.isNaN(date.getTime()) ? null : date;
}

// Formats a server datetime for display in the viewer's locale. parseServerDate
// reads the server's wall clock as if it were local time, so subtracting the
// measured skew (server wall clock minus local now) recovers the true instant.
export function formatServerDate(value, skew = 0) {
    const date = parseServerDate(value);
    return date ? new Date(date.getTime() - skew).toLocaleString() : "";
}

export function EventCountdown({label, target, skew = 0, onExpire = null}) {
    const [, setTick] = useState(0);
    const expiredRef = useRef(true);
    const onExpireRef = useRef(onExpire);

    useEffect(() => {
        onExpireRef.current = onExpire;
    }, [onExpire]);

    useEffect(() => {
        const targetDate = parseServerDate(target);
        // Only arm the expiry callback if the target is still in the future,
        // so a countdown mounted for an already-passed time never fires it.
        expiredRef.current = !targetDate || targetDate.getTime() - (Date.now() + skew) <= 0;
        const id = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(id);
    }, [target, skew]);

    const targetDate = parseServerDate(target);
    const remainingMs = targetDate ? Math.max(0, targetDate.getTime() - (Date.now() + skew)) : 0;

    useEffect(() => {
        if (remainingMs <= 0 && !expiredRef.current) {
            expiredRef.current = true;
            if (onExpireRef.current) onExpireRef.current();
        }
    }, [remainingMs]);

    const totalSeconds = Math.floor(remainingMs / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const pad = (n) => n.toString().padStart(2, "0");

    return (
        <div className="bg-neutral-800 rounded-lg p-4 text-center">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-white/50 mb-1">{label}</div>
            <div className="font-mono text-2xl text-white tabular-nums">
                {days > 0 && <span>{days}d </span>}
                {pad(hours)}:{pad(minutes)}:{pad(seconds)}
            </div>
        </div>
    );
}
